import DateFnsUtils from '@date-io/date-fns'
import { yupResolver } from '@hookform/resolvers'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { VoiceTextField } from '@ifca-root/react-component/src/components/Input/CustomTextField'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import { TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { ExitConfirmationDialog } from 'components/Dialog/ExitConfirmationDialog'
import { AccountFooter } from 'components/Footer/AccountFooter'
import SnackBarContext from 'containers/App/Store/SnackBarContext'
import {
  ApprovalStatus,
  useCreateFaProvisionImpairmentChangeMutation,
  useGetAssetRegistrationQuery,
  useGetCurrentImpairmentInfoLazyQuery,
  useGetDocumentDateValidationQuery,
  useLatestOpenPeriodCheckingDateQuery,
  useUpdateFaProvisionImpairmentChangeMutation,
} from 'generated/graphql'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import * as yup from 'yup'

interface CurrentProvisionImpairmentInfo {
  baseOriginalAmount: number
  currentOriginalValue: number
  currentAccumulatedDepreciation: number
  currentAccumulatedImpairment: number
  currentAccumulatedReversal: number
  currentNetBookValue: number
  maxAllowedImpairment: number
  quantity: number
  residualValue: number
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

export const ProvisionImpairmentForm = (props: any) => {
  const history = useHistory()
  const location = useLocation()
  const editData: any = location?.state
  const { formMode } = props
  const { CompanyID }: any = useParams()
  const user = JSON.parse(localStorage.getItem('loggedInUser'))

  /* -------------------------------------------- */
  /*                     STATE                    */
  /* -------------------------------------------- */
  const [errorDia, setErrorDia] = useState<boolean>(false)
  const [errMsg, setErrMsg] = useState<string>('')
  const [openExitConf, setOpenExitConf] = useState(false)
  const [record, setRecord] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<any>(
    editData?.Asset || null
  )
  const [
    currentInfo,
    setCurrentInfo,
  ] = useState<CurrentProvisionImpairmentInfo | null>(null)
  const { setOpenSnackBar, setSnackBarMsg }: any = useContext(SnackBarContext)

  let mode = formMode
  if (formMode === 'resubmit') {
    mode = 'edit'
  }

  /* -------------------------------------------- */
  /*                   USE FORM                   */
  /* -------------------------------------------- */
  const provisionImpairmentSchema = yup.object().shape({
    asset_no: yup.mixed().required('Asset is required'),
    change_date: yup.date().required('Change Date is required'),
    impairment_amount: yup
      .number()
      .transform((value, originalValue) => {
        if (
          originalValue === '' ||
          originalValue === null ||
          originalValue === undefined
        )
          return null
        return typeof originalValue === 'string'
          ? parseFloat(originalValue.replace(/,/g, ''))
          : originalValue
      })
      .nullable()
      .required('Provision Amount is required')
      .moreThan(0, 'Provision Amount must be greater than 0'),
    description: yup.string().required('Description is required'),
    reason: yup.string().required('Reason is required'),
  })

  const { setValue, control, handleSubmit, watch, errors } = useForm<any>({
    defaultValues: {
      change_date: editData?.change_date || new Date(),
      impairment_amount: editData?.impairment_amount || null,
      description: editData?.description || '',
      reason: editData?.reason || '',
    },
    mode: 'onSubmit',
    resolver: yupResolver(provisionImpairmentSchema),
  })

  const watchedImpairmentAmount = watch('impairment_amount')

  const calculatedNewAccumulatedImpairment = useMemo(() => {
    const impairment = parseFloat(watchedImpairmentAmount) || 0
    const oldImpairment = currentInfo?.currentAccumulatedImpairment || 0
    return oldImpairment + impairment
  }, [watchedImpairmentAmount, currentInfo])

  const calculatedNewNetBookValue = useMemo(() => {
    const impairment = parseFloat(watchedImpairmentAmount) || 0
    const oldNetBook = currentInfo?.currentNetBookValue || 0
    return Math.max(0, oldNetBook - impairment)
  }, [watchedImpairmentAmount, currentInfo])

  /* -------------------------------------------- */
  /*                    QUERY                     */
  /* -------------------------------------------- */
  const {
    loading: assetsLoading,
    data: { getAssetRegistration } = { getAssetRegistration: [] },
  } = useGetAssetRegistrationQuery({
    fetchPolicy: 'network-only',
    variables: {
      CompanyID: CompanyID,
    },
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const [
    getCurrentImpairmentInfo,
    { loading: currentInfoLoading },
  ] = useGetCurrentImpairmentInfoLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: data => {
      if (data?.getCurrentImpairmentInfo) {
        const info = data.getCurrentImpairmentInfo as CurrentProvisionImpairmentInfo
        setCurrentInfo(info)
        setValue('original_amount', info.currentOriginalValue)
        setValue('quantity', info.quantity)
        setValue(
          'old_accumulated_depreciation',
          info.currentAccumulatedDepreciation
        )
        setValue(
          'old_accumulated_impairment',
          info.currentAccumulatedImpairment
        )
        setValue('acc_impairment_reversal', info.currentAccumulatedReversal)
        setValue('residual_value', info.residualValue)
        const residualPercent =
          info.currentOriginalValue > 0
            ? (info.residualValue / info.currentOriginalValue) * 100
            : 0
        setValue('residual_percentage', residualPercent)
        setValue('old_net_book_value', info.currentNetBookValue)
      }
    },
  })

  const {
    loading: openPeriodCheckDateLoading,
    data: { latestOpenPeriodCheckingDate } = {
      latestOpenPeriodCheckingDate: {} as any,
    },
  } = useLatestOpenPeriodCheckingDateQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID },
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const {
    loading: docDateValidationLoading,
    data: { getDocumentDateValidation } = { getDocumentDateValidation: [] },
  } = useGetDocumentDateValidationQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const assetsList = useMemo(() => {
    return getAssetRegistration || []
  }, [getAssetRegistration])

  useEffect(() => {
    if (
      (formMode === 'edit' || formMode === 'resubmit') &&
      editData?.fa_asset_id &&
      assetsList.length > 0
    ) {
      const asset = assetsList.find(a => a.fa_asset_id === editData.fa_asset_id)
      if (asset) {
        setSelectedAsset(asset)
        setValue('asset_name', asset.name || '')
        setValue('start_date', asset.start_use_date)
        setValue('model_no', asset.model_no || '')

        getCurrentImpairmentInfo({
          variables: {
            fa_asset_id: asset.fa_asset_id,
            exclude_impairment_id: editData.provision_impairment_id || null,
          },
        })
      }
    }
  }, [formMode, editData, assetsList])

  /* -------------------------------------------- */
  /*                   MUTATION                   */
  /* -------------------------------------------- */
  const [
    createProvisionImpairmentChange,
    { loading: createLoading },
  ] = useCreateFaProvisionImpairmentChangeMutation({
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.createNewRecord())
      history.push(
        `/fixed-assets/${CompanyID}/assets-change/provision-for-impairment`
      )
    },
  })

  const [
    updateProvisionImpairmentChange,
    { loading: updateLoading },
  ] = useUpdateFaProvisionImpairmentChangeMutation({
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.updateRecord())
      history.push(
        `/fixed-assets/${CompanyID}/assets-change/provision-for-impairment`
      )
    },
  })

  /* -------------------------------------------- */
  /*                   HANDLERS                   */
  /* -------------------------------------------- */
  const onSubmit = (data: any, approvalStatus: ApprovalStatus) => {
    if (approvalStatus === ApprovalStatus.Submit) {
      if (isPeriodClosed) {
        setErrMsg(
          'Cannot submit. The accounting period is closed or does not exist. ' +
            'Please select a date within the open financial period ' +
            (latestOpenPeriodCheckingDate?.StartDate &&
            latestOpenPeriodCheckingDate?.EndDate
              ? `(${latestOpenPeriodCheckingDate.StartDate} to ${latestOpenPeriodCheckingDate.EndDate}).`
              : '.')
        )
        setErrorDia(true)
        return
      }

      if (isDocDateInvalid) {
        setErrMsg(
          'Cannot submit. The change date is outside the allowed validation range. ' +
            'Please select a date within the valid period.'
        )
        setErrorDia(true)
        return
      }

      if (isNBVZeroOrNegative) {
        setErrMsg(
          'Cannot submit. The asset has zero or negative Net Book Value and cannot be impaired.'
        )
        setErrorDia(true)
        return
      }
    }

    const currentAsset = selectedAsset || editData?.Asset

    if (!currentAsset) {
      setErrMsg('Please select an asset')
      setErrorDia(true)
      return
    }

    const impairmentAmount = parseFloat(data.impairment_amount) || 0

    if (impairmentAmount <= 0) {
      setErrMsg('Impairment amount must be greater than 0')
      setErrorDia(true)
      return
    }

    if (currentInfo && impairmentAmount > currentInfo.currentNetBookValue) {
      setErrMsg(
        `Impairment amount (${formatCurrency(
          impairmentAmount
        )}) cannot exceed current net book value (${formatCurrency(
          currentInfo.currentNetBookValue
        )}).`
      )
      setErrorDia(true)
      return
    }

    const input = {
      provision_impairment_id: editData?.provision_impairment_id || undefined,
      fa_asset_id: selectedAsset?.fa_asset_id || editData?.fa_asset_id,
      company_id: CompanyID,
      change_date: data.change_date,
      description: data.description,
      reason: data.reason,
      old_accumulated_depreciation:
        currentInfo?.currentAccumulatedDepreciation || 0,
      old_accumulated_impairment:
        currentInfo?.currentAccumulatedImpairment || 0,
      old_net_book_value: currentInfo?.currentNetBookValue || 0,
      impairment_amount: impairmentAmount,
      new_accumulated_impairment: calculatedNewAccumulatedImpairment,
      new_net_book_value: calculatedNewNetBookValue,
      ApprovalStatus: approvalStatus,
    }

    if (formMode === 'add') {
      createProvisionImpairmentChange({ variables: { input } })
    } else {
      updateProvisionImpairmentChange({ variables: { input } })
    }
  }

  const handleAssetChange = (value: any) => {
    if (value) {
      setSelectedAsset(value)
      setValue('asset_no', value)
      setValue('asset_name', value?.name)
      setValue('start_date', value?.start_use_date)
      setValue('model_no', value?.model_no)
      getCurrentImpairmentInfo({
        variables: {
          fa_asset_id: value.fa_asset_id,
          exclude_impairment_id: editData?.provision_impairment_id || null,
        },
      })
    } else {
      setSelectedAsset(null)
      setValue('asset_no', null)
      setCurrentInfo(null)
      setValue('original_amount', 0)
      setValue('quantity', 0)
      setValue('old_accumulated_depreciation', 0)
      setValue('old_accumulated_impairment', 0)
      setValue('acc_impairment_reversal', 0)
      setValue('residual_value', 0)
      setValue('residual_percentage', 0)
      setValue('old_net_book_value', 0)
    }
  }

  /* -------------------------------------------- */
  /*               PERIOD VALIDATION              */
  /* -------------------------------------------- */
  const watchChangeDate =
    watch('change_date') || editData?.change_date || new Date()
  const changeDateStr = (() => {
    const d = new Date(watchChangeDate)
    const year = d.getFullYear()
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const day = d
      .getDate()
      .toString()
      .padStart(2, '0')
    return `${year}-${month}-${day}`
  })()

  const isPeriodClosed =
    latestOpenPeriodCheckingDate?.StartDate &&
    latestOpenPeriodCheckingDate?.EndDate
      ? changeDateStr < latestOpenPeriodCheckingDate?.StartDate ||
        changeDateStr > latestOpenPeriodCheckingDate?.EndDate
      : false

  const today = new Date()
  const monthsBefore = getDocumentDateValidation?.[0]?.MonthsBefore ?? 12
  const monthsAfter = getDocumentDateValidation?.[0]?.MonthsAfter ?? 1

  const minValidDate = new Date(today)
  minValidDate.setMonth(minValidDate.getMonth() - monthsBefore)
  minValidDate.setHours(0, 0, 0, 0)

  const maxValidDate = new Date(today)
  maxValidDate.setMonth(maxValidDate.getMonth() + monthsAfter)
  maxValidDate.setHours(23, 59, 59, 999)

  const changeDate = new Date(watchChangeDate)
  const isDocDateInvalid =
    changeDate.getTime() < minValidDate.getTime() ||
    changeDate.getTime() > maxValidDate.getTime()

  const isNBVZeroOrNegative =
    currentInfo && currentInfo.currentNetBookValue <= 0

  /* -------------------------------------------- */
  /*                    FOOTER                    */
  /* -------------------------------------------- */
  const draftFooterOption = {
    name: 'Save as Draft',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Active)),
    color: 'primary',
    props: { type: 'button' },
  }

  const submitFooterOption = {
    name: 'Submit',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Submit)),
    color: 'primary',
    props: { type: 'button' },
  }

  let footerOptions: any[]
  if (formMode === 'add' || formMode === 'edit' || formMode === 'resubmit') {
    footerOptions = [draftFooterOption, submitFooterOption]
  } else {
    footerOptions = []
  }

  return (
    <>
      {(assetsLoading ||
        createLoading ||
        updateLoading ||
        currentInfoLoading ||
        openPeriodCheckDateLoading ||
        docDateValidationLoading) && <Loading />}

      <MainHeader
        mainBtn="close"
        onClick={() => {
          history.push(
            `/fixed-assets/${CompanyID}/assets-change/provision-for-impairment`
          )
        }}
        smTitle={'Fixed Assets'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Fixed Assets' },
          { name: 'Assets Change' },
          { name: 'Provision Impairment', current: true },
        ]}
        rightRouteSegments={[
          {
            name:
              formMode === 'add'
                ? 'New'
                : formMode === 'resubmit'
                ? 'Resubmit'
                : 'Edit',
            current: true,
          },
        ]}
      />

      <ContentWrapper float footer>
        <CardContents
          section={{
            header: { title: 'Assets Information' },
          }}
        >
          <Controller
            render={({ value, onChange }) => {
              const defVal = editData?.Asset || null

              return (
                <Autocomplete
                  options={
                    assetsList?.sort((a, b) => {
                      return a?.DocNo?.localeCompare(b?.DocNo)
                    }) || []
                  }
                  getOptionLabel={option =>
                    `${option?.DocNo} - ${option?.name}`
                  }
                  fullWidth
                  onChange={(event, value: any) => handleAssetChange(value)}
                  defaultValue={defVal}
                  disabled={mode === 'approve-reject'}
                  renderOption={props => {
                    return (
                      <div>
                        <div>
                          <span className="xsTitle">{props?.DocNo}</span>
                          <span className="desc" style={{ marginLeft: '8px' }}>
                            {props?.name}
                          </span>
                        </div>
                      </div>
                    )
                  }}
                  renderInput={(params: any) => {
                    return (
                      <TextField
                        {...params}
                        label="Asset"
                        style={{ width: '100%' }}
                        required
                        margin="normal"
                        helperText={errors?.asset_no?.message}
                        error={errors?.asset_no ? true : false}
                      />
                    )
                  }}
                />
              )
            }}
            name="asset_no"
            control={control}
            margin="normal"
            defaultValue={editData?.Asset || null}
          />

          <Controller
            as={TextField}
            name="asset_name"
            label="Asset Name"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            defaultValue={editData?.Asset?.name || 'N/A'}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              render={({ value, onChange }) => (
                <KeyboardDatePicker
                  value={value}
                  onChange={onChange}
                  label="Start Date"
                  format="dd/MM/yyyy"
                  readOnly
                  margin="dense"
                  allowKeyboardControl
                  KeyboardButtonProps={{
                    style: { display: 'none' },
                  }}
                  className="left"
                  InputProps={{
                    disableUnderline: true,
                    readOnly: true,
                  }}
                />
              )}
              name="start_date"
              control={control}
              defaultValue={editData?.Asset?.start_use_date || new Date()}
            />
          </MuiPickersUtilsProvider>

          <Controller
            as={TextField}
            name="model_no"
            label="Model No."
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            className="right"
            defaultValue={editData?.Asset?.model_no || 'N/A'}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={currentInfo?.currentOriginalValue ?? value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Original Amount"
                fullWidth
                margin="dense"
                className="left"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="original_amount"
            control={control}
            defaultValue={editData?.Asset?.base_original_amount || 0}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={currentInfo?.quantity ?? value}
                thousandSeparator
                decimalScale={0}
                label="Quantity"
                fullWidth
                margin="dense"
                className="right"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="quantity"
            control={control}
            defaultValue={editData?.Asset?.quantity || 0}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={currentInfo?.currentAccumulatedDepreciation ?? value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Acc. Depreciated Amount"
                fullWidth
                margin="dense"
                className="left"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="old_accumulated_depreciation"
            control={control}
            defaultValue={editData?.old_accumulated_depreciation || 0}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={currentInfo?.currentAccumulatedImpairment ?? value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Accumulated Impairment"
                fullWidth
                margin="dense"
                className="right"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="old_accumulated_impairment"
            control={control}
            defaultValue={editData?.old_accumulated_impairment || 0}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={currentInfo?.currentAccumulatedReversal ?? value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Acc. Impairment Reversal"
                fullWidth
                margin="dense"
                className="left"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="acc_impairment_reversal"
            control={control}
            defaultValue={0}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={currentInfo?.residualValue ?? value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Residual Value"
                fullWidth
                margin="dense"
                className="right"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="residual_value"
            control={control}
            defaultValue={0}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={
                  currentInfo && currentInfo.currentOriginalValue > 0
                    ? (currentInfo.residualValue /
                        currentInfo.currentOriginalValue) *
                      100
                    : value
                }
                thousandSeparator
                decimalScale={4}
                fixedDecimalScale
                suffix="%"
                label="Residual (%)"
                fullWidth
                margin="dense"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="residual_percentage"
            control={control}
            defaultValue={0}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={currentInfo?.currentNetBookValue ?? value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Current Net Book Value"
                fullWidth
                margin="dense"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
                helperText={
                  currentInfo
                    ? `Max impairment: ${formatCurrency(
                        currentInfo.currentNetBookValue
                      )}`
                    : ''
                }
              />
            )}
            name="old_net_book_value"
            control={control}
            defaultValue={editData?.old_net_book_value || 0}
          />
        </CardContents>

        <CardContents
          section={{
            header: { title: 'Provision for Impairment' },
          }}
        >
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              render={({ value, onChange }) => (
                <KeyboardDatePicker
                  value={value}
                  onChange={onChange}
                  label="Date"
                  format="dd/MM/yyyy"
                  margin="dense"
                  allowKeyboardControl
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  className="left"
                  required
                  disabled={mode === 'approve-reject'}
                />
              )}
              name="change_date"
              control={control}
              defaultValue={editData?.change_date || new Date()}
            />
          </MuiPickersUtilsProvider>

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                onValueChange={({ floatValue }) => onChange(floatValue)}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Provision Amount"
                fullWidth
                margin="dense"
                className="right"
                required
                disabled={mode === 'approve-reject'}
                helperText={errors?.impairment_amount?.message}
                error={errors?.impairment_amount ? true : false}
              />
            )}
            name="impairment_amount"
            control={control}
            defaultValue={editData?.impairment_amount || ''}
          />

          <NumberFormat
            customInput={TextField as any}
            value={calculatedNewAccumulatedImpairment}
            thousandSeparator
            decimalScale={2}
            fixedDecimalScale
            label="New Accumulated Impairment"
            fullWidth
            margin="dense"
            className="left"
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <NumberFormat
            customInput={TextField as any}
            value={calculatedNewNetBookValue}
            thousandSeparator
            decimalScale={2}
            fixedDecimalScale
            label="New Net Book Value"
            fullWidth
            margin="dense"
            className="right"
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <VoiceTextField
            mounted={true}
            label="Description"
            name="description"
            setValue={setValue}
            record={record}
            setRecord={setRecord}
            control={control}
            controllerProps={{
              autoComplete: 'off',
              defaultValue: editData?.description || '',
              helperText: errors?.description?.message,
              error: errors?.description ? true : false,
            }}
            disabled={mode === 'approve-reject'}
            required
          />

          <VoiceTextField
            mounted={true}
            label="Reason"
            name="reason"
            setValue={setValue}
            record={record}
            setRecord={setRecord}
            control={control}
            controllerProps={{
              autoComplete: 'off',
              defaultValue: editData?.reason || '',
              helperText: errors?.reason?.message,
              error: errors?.reason ? true : false,
            }}
            disabled={mode === 'approve-reject'}
            required
          />
        </CardContents>
      </ContentWrapper>

      {footerOptions?.length > 0 && (
        <AccountFooter options={[...footerOptions]} />
      )}

      <ErrorDialog
        errorDia={errorDia}
        setErrorDia={setErrorDia}
        errorMsg={errMsg}
        errorHeaderMsg={'Error'}
      />

      <ExitConfirmationDialog
        openExitConf={openExitConf}
        setOpenExitConf={setOpenExitConf}
        onConfirm={() => {
          history.push(
            `/fixed-assets/${CompanyID}/assets-change/provision-for-impairment`
          )
        }}
      />
    </>
  )
}
